import type { SVGProps } from 'react';

/**
 * Phase 6.5 — Type-specific stylised silhouettes.
 *
 * Conventions:
 *   - viewBox 240×120 (matches the generic Fire/Ems/Special silhouettes
 *     which used 220×90 — close enough; both look fine inside the
 *     VehicleDetail banner / purchase preview cards).
 *   - Body uses fill="currentColor"; one accent stroke (white or contrasting)
 *     conveys the type-specific detail (ladder, tank, drum, cross, …).
 *   - No gradients, no shadows. ≤80 paths each.
 */

const SHARED_DEFS = {
  bodyFill: 'currentColor',
  windowFill: 'rgba(255,255,255,0.85)',
  wheelFill: '#1f2937',
  hubFill: '#94a3b8',
  accent: '#ffffff',
};

function Wheels(): JSX.Element {
  return (
    <g>
      <ellipse cx="120" cy="110" rx="100" ry="3" fill="#000" opacity="0.25" />
      <circle cx="50" cy="100" r="11" fill={SHARED_DEFS.wheelFill} />
      <circle cx="50" cy="100" r="4" fill={SHARED_DEFS.hubFill} />
      <circle cx="190" cy="100" r="11" fill={SHARED_DEFS.wheelFill} />
      <circle cx="190" cy="100" r="4" fill={SHARED_DEFS.hubFill} />
    </g>
  );
}

/* ───────────────────── Fire ───────────────────── */

export function ElwSilhouette(props: SVGProps<SVGSVGElement>) {
  // Compact command car, square cab + box back
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <rect x="20" y="60" width="200" height="40" rx="4" fill={SHARED_DEFS.bodyFill} />
      <rect x="40" y="40" width="100" height="22" rx="3" fill={SHARED_DEFS.bodyFill} />
      <rect x="46" y="44" width="32" height="14" fill={SHARED_DEFS.windowFill} />
      <rect x="84" y="44" width="50" height="14" fill={SHARED_DEFS.windowFill} />
      <rect x="160" y="50" width="44" height="10" fill={SHARED_DEFS.windowFill} />
      {/* Antenna */}
      <line x1="64" y1="40" x2="64" y2="22" stroke={SHARED_DEFS.accent} strokeWidth="2" />
      <circle cx="64" cy="22" r="2.2" fill={SHARED_DEFS.accent} />
      {/* Pin stripe */}
      <rect x="20" y="78" width="200" height="3" fill={SHARED_DEFS.accent} opacity="0.85" />
    </svg>
  );
}

export function MtwSilhouette(props: SVGProps<SVGSVGElement>) {
  // Long bus / Mannschaftstransport — stretched body + 2 rows of windows
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <path
        d="M14 50 Q14 36 28 36 L218 36 Q230 36 230 50 L230 100 L14 100 Z"
        fill={SHARED_DEFS.bodyFill}
      />
      <rect x="20" y="44" width="40" height="20" fill={SHARED_DEFS.windowFill} />
      <rect x="68" y="44" width="40" height="20" fill={SHARED_DEFS.windowFill} />
      <rect x="116" y="44" width="40" height="20" fill={SHARED_DEFS.windowFill} />
      <rect x="164" y="44" width="40" height="20" fill={SHARED_DEFS.windowFill} />
      <rect x="14" y="80" width="216" height="3" fill={SHARED_DEFS.accent} opacity="0.85" />
    </svg>
  );
}

export function DlkSilhouette(props: SVGProps<SVGSVGElement>) {
  // Big body with extending ladder on top
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <rect x="14" y="56" width="210" height="44" rx="4" fill={SHARED_DEFS.bodyFill} />
      <path d="M14 56 L14 36 L60 36 L72 56 Z" fill={SHARED_DEFS.bodyFill} />
      <path d="M22 40 L56 40 L66 54 L22 54 Z" fill={SHARED_DEFS.windowFill} />
      <rect x="14" y="78" width="210" height="3" fill={SHARED_DEFS.accent} opacity="0.85" />
      {/* Ladder on top */}
      <line x1="80" y1="36" x2="220" y2="14" stroke={SHARED_DEFS.accent} strokeWidth="2.5" />
      <line x1="84" y1="42" x2="220" y2="22" stroke={SHARED_DEFS.accent} strokeWidth="2.5" />
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <line
          key={i}
          x1={90 + i * 20}
          y1={40 - i * 1.5}
          x2={90 + i * 20}
          y2={32 - i * 1.5}
          stroke={SHARED_DEFS.accent}
          strokeWidth="2.5"
        />
      ))}
    </svg>
  );
}

export function PkwSilhouette(props: SVGProps<SVGSVGElement>) {
  // Small command sedan — low + long with sloped roof
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <path
        d="M20 100 L20 80 Q20 70 30 68 L70 56 Q80 50 100 50 L160 50 Q176 50 188 60 L218 70 Q224 76 224 84 L224 100 Z"
        fill={SHARED_DEFS.bodyFill}
      />
      <path d="M84 56 L156 56 L182 70 L80 70 Z" fill={SHARED_DEFS.windowFill} />
      <rect x="22" y="88" width="200" height="3" fill={SHARED_DEFS.accent} opacity="0.7" />
      {/* Light bar */}
      <rect x="100" y="46" width="42" height="4" rx="1" fill="#3b82f6" />
    </svg>
  );
}

export function TlfSilhouette(props: SVGProps<SVGSVGElement>) {
  // Big rounded water tank dominating the back
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <rect x="20" y="60" width="40" height="40" rx="3" fill={SHARED_DEFS.bodyFill} />
      <rect x="22" y="48" width="40" height="14" rx="3" fill={SHARED_DEFS.bodyFill} />
      <rect x="26" y="50" width="32" height="10" fill={SHARED_DEFS.windowFill} />
      {/* Cylindrical tank */}
      <rect x="62" y="48" width="160" height="56" rx="20" fill={SHARED_DEFS.bodyFill} />
      <ellipse cx="222" cy="76" rx="6" ry="20" fill="#000" opacity="0.2" />
      <line x1="80" y1="78" x2="200" y2="78" stroke={SHARED_DEFS.accent} strokeWidth="2.5" opacity="0.8" />
      {/* Water droplet badge */}
      <circle cx="140" cy="76" r="9" fill={SHARED_DEFS.accent} opacity="0.85" />
      <text x="140" y="80" textAnchor="middle" fontSize="10" fontWeight="700" fill={SHARED_DEFS.bodyFill}>
        H₂O
      </text>
    </svg>
  );
}

export function HlfSilhouette(props: SVGProps<SVGSVGElement>) {
  // Compact body with pump module + hose drum detail
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <rect x="14" y="56" width="210" height="44" rx="4" fill={SHARED_DEFS.bodyFill} />
      <path d="M14 56 L14 38 L66 38 L80 56 Z" fill={SHARED_DEFS.bodyFill} />
      <path d="M22 42 L62 42 L74 54 L22 54 Z" fill={SHARED_DEFS.windowFill} />
      <rect x="14" y="78" width="210" height="3" fill={SHARED_DEFS.accent} opacity="0.85" />
      {/* Pump-bay door */}
      <rect x="92" y="62" width="50" height="32" rx="2" fill="rgba(0,0,0,0.25)" />
      {/* Hose drum (circle) */}
      <circle cx="184" cy="76" r="14" fill="rgba(0,0,0,0.25)" />
      <circle cx="184" cy="76" r="4" fill={SHARED_DEFS.accent} />
      {/* Light bar */}
      <rect x="34" y="34" width="36" height="5" rx="2" fill="#3b82f6" />
    </svg>
  );
}

export function LfSilhouette(props: SVGProps<SVGSVGElement>) {
  // Standard fire engine — generic but slightly distinct from HLF
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <rect x="14" y="58" width="210" height="42" rx="4" fill={SHARED_DEFS.bodyFill} />
      <path d="M14 58 L14 40 L66 40 L78 58 Z" fill={SHARED_DEFS.bodyFill} />
      <path d="M22 44 L62 44 L72 56 L22 56 Z" fill={SHARED_DEFS.windowFill} />
      <rect x="14" y="78" width="210" height="3" fill={SHARED_DEFS.accent} opacity="0.85" />
      {/* Compartment lines */}
      <line x1="100" y1="66" x2="100" y2="98" stroke="rgba(0,0,0,0.3)" strokeWidth="1.4" />
      <line x1="148" y1="66" x2="148" y2="98" stroke="rgba(0,0,0,0.3)" strokeWidth="1.4" />
      <line x1="190" y1="66" x2="190" y2="98" stroke="rgba(0,0,0,0.3)" strokeWidth="1.4" />
      <rect x="34" y="36" width="36" height="5" rx="2" fill="#3b82f6" />
    </svg>
  );
}

export function GwSilhouette(props: SVGProps<SVGSVGElement>) {
  // Box-body equipment carrier; big "AS" badge
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <rect x="60" y="40" width="160" height="60" rx="4" fill={SHARED_DEFS.bodyFill} />
      <path d="M14 50 L14 100 L60 100 L60 40 L34 40 Q22 40 18 48 Z" fill={SHARED_DEFS.bodyFill} />
      <path d="M22 52 L52 52 L52 70 L22 70 Z" fill={SHARED_DEFS.windowFill} />
      <rect x="14" y="80" width="206" height="3" fill={SHARED_DEFS.accent} opacity="0.85" />
      {/* Storage compartment grid */}
      <g stroke="rgba(0,0,0,0.3)" strokeWidth="1.2">
        <line x1="100" y1="48" x2="100" y2="92" />
        <line x1="140" y1="48" x2="140" y2="92" />
        <line x1="180" y1="48" x2="180" y2="92" />
      </g>
      {/* Atemschutz badge */}
      <circle cx="200" cy="58" r="9" fill={SHARED_DEFS.accent} />
      <text x="200" y="62" textAnchor="middle" fontSize="10" fontWeight="700" fill={SHARED_DEFS.bodyFill}>
        AS
      </text>
    </svg>
  );
}

export function RwSilhouette(props: SVGProps<SVGSVGElement>) {
  // Rüstwagen with crane stub
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <rect x="14" y="58" width="210" height="42" rx="4" fill={SHARED_DEFS.bodyFill} />
      <path d="M14 58 L14 38 L60 38 L72 58 Z" fill={SHARED_DEFS.bodyFill} />
      <path d="M20 42 L56 42 L66 56 L20 56 Z" fill={SHARED_DEFS.windowFill} />
      <rect x="14" y="78" width="210" height="3" fill={SHARED_DEFS.accent} opacity="0.85" />
      {/* Crane arm folded above */}
      <path
        d="M120 60 L210 26 L218 30 L130 64 Z"
        fill={SHARED_DEFS.bodyFill}
        opacity="0.85"
      />
      <line x1="120" y1="60" x2="218" y2="30" stroke={SHARED_DEFS.accent} strokeWidth="1.5" opacity="0.7" />
      <circle cx="120" cy="60" r="4" fill={SHARED_DEFS.accent} opacity="0.85" />
    </svg>
  );
}

export function SwSilhouette(props: SVGProps<SVGSVGElement>) {
  // Schlauchwagen — visible big hose drum on the back
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <rect x="14" y="58" width="200" height="42" rx="4" fill={SHARED_DEFS.bodyFill} />
      <path d="M14 58 L14 40 L60 40 L72 58 Z" fill={SHARED_DEFS.bodyFill} />
      <path d="M22 44 L56 44 L66 56 L22 56 Z" fill={SHARED_DEFS.windowFill} />
      <rect x="14" y="78" width="200" height="3" fill={SHARED_DEFS.accent} opacity="0.85" />
      {/* Big hose drum at back */}
      <circle cx="180" cy="78" r="20" fill="rgba(0,0,0,0.3)" />
      <circle cx="180" cy="78" r="14" fill={SHARED_DEFS.bodyFill} />
      <circle cx="180" cy="78" r="6" fill={SHARED_DEFS.accent} opacity="0.85" />
      {/* Coil texture */}
      <circle cx="180" cy="78" r="10" fill="none" stroke={SHARED_DEFS.accent} strokeWidth="0.8" opacity="0.5" />
    </svg>
  );
}

/* Phase 9.6.5 — Feuerwehrkran (FWK) — heavier crane than RW */
export function FwkSilhouette(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      {/* Heavy chassis */}
      <rect x="14" y="62" width="216" height="38" rx="3" fill={SHARED_DEFS.bodyFill} />
      <path d="M14 62 L14 42 L58 42 L70 62 Z" fill={SHARED_DEFS.bodyFill} />
      <path d="M20 46 L54 46 L64 60 L20 60 Z" fill={SHARED_DEFS.windowFill} />
      <rect x="14" y="80" width="216" height="3" fill={SHARED_DEFS.accent} opacity="0.85" />
      {/* Telescoping crane (longer than RW) */}
      <path
        d="M120 64 L228 16 L236 22 L130 70 Z"
        fill={SHARED_DEFS.bodyFill}
        opacity="0.9"
      />
      <line x1="120" y1="64" x2="236" y2="22" stroke={SHARED_DEFS.accent} strokeWidth="1.8" opacity="0.7" />
      <circle cx="120" cy="64" r="6" fill={SHARED_DEFS.accent} opacity="0.9" />
      {/* Outrigger stabilisers */}
      <rect x="60" y="98" width="14" height="6" fill="rgba(0,0,0,0.4)" />
      <rect x="166" y="98" width="14" height="6" fill="rgba(0,0,0,0.4)" />
    </svg>
  );
}

export function TsfSilhouette(props: SVGProps<SVGSVGElement>) {
  // Smaller transporter-style fire engine
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <rect x="40" y="56" width="170" height="44" rx="4" fill={SHARED_DEFS.bodyFill} />
      <path d="M40 56 L40 42 L86 42 L96 56 Z" fill={SHARED_DEFS.bodyFill} />
      <path d="M48 46 L80 46 L90 54 L48 54 Z" fill={SHARED_DEFS.windowFill} />
      <rect x="40" y="78" width="170" height="3" fill={SHARED_DEFS.accent} opacity="0.85" />
      {/* Compact pump */}
      <rect x="110" y="62" width="40" height="30" rx="2" fill="rgba(0,0,0,0.22)" />
      <rect x="58" y="38" width="22" height="4" rx="2" fill="#3b82f6" />
    </svg>
  );
}

/* ───────────────────── EMS ───────────────────── */

export function KtwSilhouette(props: SVGProps<SVGSVGElement>) {
  // Slightly smaller transport box
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <rect x="60" y="42" width="160" height="58" rx="4" fill={SHARED_DEFS.bodyFill} />
      <path d="M14 50 L14 100 L60 100 L60 42 L34 42 Q22 42 18 50 Z" fill={SHARED_DEFS.bodyFill} />
      <path d="M22 52 L52 52 L52 68 L22 68 Z" fill={SHARED_DEFS.windowFill} />
      <rect x="80" y="50" width="22" height="14" fill={SHARED_DEFS.windowFill} />
      <rect x="108" y="50" width="22" height="14" fill={SHARED_DEFS.windowFill} />
      <rect x="14" y="78" width="206" height="3" fill={SHARED_DEFS.accent} opacity="0.85" />
      {/* Star of life */}
      <g transform="translate(170 70)" fill="#0ea5e9">
        <circle r="9" fill={SHARED_DEFS.accent} />
        <rect x="-6" y="-1.5" width="12" height="3" />
        <rect x="-1.5" y="-6" width="3" height="12" />
        <rect x="-6" y="-1.5" width="12" height="3" transform="rotate(60)" />
        <rect x="-6" y="-1.5" width="12" height="3" transform="rotate(-60)" />
      </g>
    </svg>
  );
}

export function RtwSilhouette(props: SVGProps<SVGSVGElement>) {
  // Big patient box, prominent star-of-life
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <rect x="60" y="34" width="170" height="66" rx="4" fill={SHARED_DEFS.bodyFill} />
      <path d="M14 50 L14 100 L60 100 L60 34 L34 34 Q22 34 18 48 Z" fill={SHARED_DEFS.bodyFill} />
      <path d="M22 52 L52 52 L52 70 L22 70 Z" fill={SHARED_DEFS.windowFill} />
      <rect x="80" y="46" width="22" height="14" fill={SHARED_DEFS.windowFill} />
      <rect x="108" y="46" width="22" height="14" fill={SHARED_DEFS.windowFill} />
      <rect x="136" y="46" width="22" height="14" fill={SHARED_DEFS.windowFill} />
      <rect x="14" y="78" width="216" height="4" fill={SHARED_DEFS.accent} opacity="0.9" />
      {/* Light bar */}
      <rect x="80" y="28" width="120" height="6" rx="2" fill="#3b82f6" />
      {/* Star of life */}
      <g transform="translate(190 64)" fill="#0ea5e9">
        <circle r="11" fill={SHARED_DEFS.accent} />
        <rect x="-8" y="-2" width="16" height="4" />
        <rect x="-2" y="-8" width="4" height="16" />
        <rect x="-8" y="-2" width="16" height="4" transform="rotate(60)" />
        <rect x="-8" y="-2" width="16" height="4" transform="rotate(-60)" />
      </g>
    </svg>
  );
}

export function NefSilhouette(props: SVGProps<SVGSVGElement>) {
  // Sedan with red cross — emergency physician car
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <path
        d="M20 100 L20 80 Q20 68 32 66 L72 56 Q86 50 110 50 L150 50 Q172 50 184 60 L222 72 Q228 78 228 86 L228 100 Z"
        fill={SHARED_DEFS.bodyFill}
      />
      <path d="M82 56 L148 56 L178 72 L78 72 Z" fill={SHARED_DEFS.windowFill} />
      <rect x="22" y="88" width="206" height="3" fill={SHARED_DEFS.accent} opacity="0.85" />
      {/* Light bar */}
      <rect x="98" y="46" width="46" height="4" rx="1" fill="#3b82f6" />
      {/* Big cross on the side */}
      <g transform="translate(150 80)">
        <rect x="-3" y="-12" width="6" height="24" fill={SHARED_DEFS.accent} />
        <rect x="-12" y="-3" width="24" height="6" fill={SHARED_DEFS.accent} />
      </g>
    </svg>
  );
}

/* ───────────────────── Polizei (Phase 10.2) ───────────────────── */

export function FuStWSilhouette(props: SVGProps<SVGSVGElement>) {
  // Sedan, blue+silver scheme via currentColor (caller supplies blue tint).
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <path
        d="M20 100 L20 78 Q20 66 36 64 L74 54 Q92 48 118 48 L156 48 Q176 48 190 58 L222 70 Q228 76 228 84 L228 100 Z"
        fill={SHARED_DEFS.bodyFill}
      />
      <path d="M84 54 L154 54 L184 70 L80 70 Z" fill={SHARED_DEFS.windowFill} />
      {/* Light bar */}
      <rect x="98" y="44" width="46" height="4" rx="1" fill="#3b82f6" />
      {/* Side stripe */}
      <rect x="22" y="80" width="206" height="6" fill="#1e293b" opacity="0.45" />
      <rect x="22" y="86" width="206" height="2" fill="#fbbf24" opacity="0.9" />
      {/* "POLIZEI" badge zone */}
      <rect x="100" y="78" width="40" height="6" fill={SHARED_DEFS.accent} opacity="0.85" />
    </svg>
  );
}

export function GruKWSilhouette(props: SVGProps<SVGSVGElement>) {
  // Box-style minibus
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <rect x="14" y="44" width="216" height="56" rx="4" fill={SHARED_DEFS.bodyFill} />
      <path d="M14 44 L14 32 L60 32 L72 44 Z" fill={SHARED_DEFS.bodyFill} />
      <path d="M20 36 L56 36 L66 42 L20 42 Z" fill={SHARED_DEFS.windowFill} />
      <rect x="80" y="50" width="22" height="14" fill={SHARED_DEFS.windowFill} />
      <rect x="108" y="50" width="22" height="14" fill={SHARED_DEFS.windowFill} />
      <rect x="136" y="50" width="22" height="14" fill={SHARED_DEFS.windowFill} />
      <rect x="164" y="50" width="22" height="14" fill={SHARED_DEFS.windowFill} />
      <rect x="14" y="78" width="216" height="4" fill="#1e293b" opacity="0.55" />
      <rect x="14" y="82" width="216" height="2" fill="#fbbf24" opacity="0.9" />
      <rect x="80" y="26" width="80" height="6" rx="2" fill="#3b82f6" />
    </svg>
  );
}

export function KradPolSilhouette(props: SVGProps<SVGSVGElement>) {
  // Side-on motorcycle with rider blob
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="120" cy="110" rx="80" ry="3" fill="#000" opacity="0.25" />
      {/* Wheels */}
      <circle cx="64" cy="92" r="14" fill={SHARED_DEFS.wheelFill} />
      <circle cx="64" cy="92" r="5" fill={SHARED_DEFS.hubFill} />
      <circle cx="180" cy="92" r="14" fill={SHARED_DEFS.wheelFill} />
      <circle cx="180" cy="92" r="5" fill={SHARED_DEFS.hubFill} />
      {/* Frame */}
      <path d="M70 86 L120 60 L160 86 Z" fill={SHARED_DEFS.bodyFill} />
      <path d="M120 60 L132 40 L150 36 L160 50 L150 60 Z" fill={SHARED_DEFS.bodyFill} />
      {/* Rider */}
      <circle cx="118" cy="40" r="9" fill={SHARED_DEFS.accent} />
      <path d="M104 64 L132 64 L138 88 L98 88 Z" fill="#1e293b" />
      {/* Blue light flash */}
      <rect x="142" y="32" width="14" height="4" rx="1" fill="#3b82f6" />
    </svg>
  );
}

export function WaWeSilhouette(props: SVGProps<SVGSVGElement>) {
  // Heavy water cannon truck
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <rect x="14" y="50" width="216" height="50" rx="3" fill={SHARED_DEFS.bodyFill} />
      <path d="M14 50 L14 30 L56 30 L70 50 Z" fill={SHARED_DEFS.bodyFill} />
      <path d="M20 34 L52 34 L62 48 L20 48 Z" fill={SHARED_DEFS.windowFill} />
      <rect x="14" y="78" width="216" height="4" fill="#1e293b" opacity="0.55" />
      <rect x="14" y="82" width="216" height="2" fill="#fbbf24" opacity="0.9" />
      {/* Water cannon barrel on the roof */}
      <rect x="120" y="22" width="6" height="14" fill={SHARED_DEFS.bodyFill} />
      <rect x="124" y="18" width="60" height="6" rx="2" fill="#1e293b" />
      <circle cx="184" cy="21" r="3" fill={SHARED_DEFS.accent} />
      {/* Tank shape on the side */}
      <rect x="84" y="58" width="80" height="14" rx="3" fill="#1e3a8a" opacity="0.45" />
    </svg>
  );
}

export function SEKWgSilhouette(props: SVGProps<SVGSVGElement>) {
  // Heavy armored van
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <rect x="14" y="40" width="216" height="60" rx="3" fill={SHARED_DEFS.bodyFill} />
      <path d="M14 40 L14 28 L60 28 L74 40 Z" fill={SHARED_DEFS.bodyFill} />
      {/* Slits instead of full windows (armored) */}
      <rect x="22" y="34" width="32" height="3" fill={SHARED_DEFS.windowFill} />
      <rect x="80" y="46" width="20" height="6" fill={SHARED_DEFS.windowFill} />
      <rect x="106" y="46" width="20" height="6" fill={SHARED_DEFS.windowFill} />
      <rect x="132" y="46" width="20" height="6" fill={SHARED_DEFS.windowFill} />
      <rect x="14" y="78" width="216" height="4" fill="#0f172a" opacity="0.7" />
      {/* Mean roof rack */}
      <rect x="40" y="22" width="160" height="6" rx="1" fill="#0f172a" />
    </svg>
  );
}

export function MEKWgSilhouette(props: SVGProps<SVGSVGElement>) {
  // Civilian-looking van — no light bar
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <Wheels />
      <rect x="20" y="44" width="206" height="56" rx="4" fill={SHARED_DEFS.bodyFill} />
      <path d="M20 44 L20 30 L62 30 L74 44 Z" fill={SHARED_DEFS.bodyFill} />
      <path d="M28 36 L58 36 L66 42 L28 42 Z" fill={SHARED_DEFS.windowFill} />
      <rect x="86" y="50" width="22" height="14" fill={SHARED_DEFS.windowFill} />
      <rect x="114" y="50" width="22" height="14" fill={SHARED_DEFS.windowFill} />
      <rect x="142" y="50" width="22" height="14" fill={SHARED_DEFS.windowFill} />
      {/* Subtle pin stripe, no police markings */}
      <rect x="20" y="82" width="206" height="2" fill="#0f172a" opacity="0.4" />
    </svg>
  );
}

export function PolHeloSilhouette(props: SVGProps<SVGSVGElement>) {
  // Side-on helicopter
  return (
    <svg viewBox="0 0 240 120" xmlns="http://www.w3.org/2000/svg" {...props}>
      <ellipse cx="120" cy="108" rx="80" ry="4" fill="#000" opacity="0.25" />
      {/* Main body */}
      <path
        d="M40 70 Q40 50 70 50 L150 50 Q170 50 174 60 L210 64 L210 76 L60 76 Q40 76 40 70 Z"
        fill={SHARED_DEFS.bodyFill}
      />
      <path d="M70 56 L130 56 L162 64 L70 64 Z" fill={SHARED_DEFS.windowFill} />
      {/* Tail boom */}
      <path d="M170 64 L226 60 L228 66 L172 70 Z" fill={SHARED_DEFS.bodyFill} />
      {/* Tail rotor */}
      <circle cx="226" cy="62" r="4" fill="#0f172a" />
      <line x1="220" y1="56" x2="232" y2="68" stroke="#0f172a" strokeWidth="1.5" />
      {/* Skids */}
      <line x1="60" y1="86" x2="180" y2="86" stroke="#0f172a" strokeWidth="2" />
      <line x1="70" y1="76" x2="70" y2="86" stroke="#0f172a" strokeWidth="2" />
      <line x1="170" y1="76" x2="170" y2="86" stroke="#0f172a" strokeWidth="2" />
      {/* Main rotor */}
      <line x1="40" y1="40" x2="200" y2="40" stroke="#0f172a" strokeWidth="3" />
      <circle cx="120" cy="40" r="4" fill="#0f172a" />
    </svg>
  );
}
